import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import FormField from '../components/ui/FormField';
import { validate, required, email } from '../utils/validation';
import { api } from '../api/client';
import Button from '../components/ui/Button';
import '../App.css';

const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const { tenant } = useAuth();

  const [emailValue, setEmailValue] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    const emailError = validate(emailValue, [
      required(t('validation.emailRequired')),
      email(t('validation.emailInvalid'))
    ]);
    if (emailError) newErrors.email = emailError;

    setFieldErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submitHandler = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      await api.auth.forgotPassword(emailValue);
      // Always show success for security (don't reveal if email exists)
      setIsSubmitted(true);
    } catch {
      // Still show success message for security reasons
      setIsSubmitted(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="page-content">
      <div className="form-container">
        {/* Show organization name if on tenant subdomain */}
        {tenant.name && (
          <div className="organization-badge">
            {tenant.name}
          </div>
        )}

        <h2>{t('auth.forgotPasswordTitle')}</h2>

        {isSubmitted ? (
          <>
            <div className="success-message" role="status">
              {t('auth.resetEmailSent')}
            </div>
            <Link to="/login" className="form-link">
              {t('auth.backToLogin')}
            </Link>
          </>
        ) : (
          <>
            <p style={{ marginBottom: '1.5rem', color: 'var(--text-secondary, #aaa)' }}>
              {t('auth.forgotPasswordDescription')}
            </p>

            <form onSubmit={submitHandler}>
              <FormField
                htmlFor="email"
                label={t('common.email')}
                error={fieldErrors.email}
                required
              >
                <input
                  type="email"
                  id="email"
                  placeholder={t('auth.enterEmail')}
                  value={emailValue}
                  onChange={(e) => {
                    setEmailValue(e.target.value);
                    if (fieldErrors.email) setFieldErrors(prev => ({ ...prev, email: '' }));
                  }}
                  disabled={isLoading}
                />
              </FormField>
              <Button type="submit" fullWidth loading={isLoading}>
                {isLoading ? t('common.loading') : t('auth.sendResetLink')}
              </Button>
            </form>

            <Link to="/login" className="form-link">
              {t('auth.backToLogin')}
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
